/**
 * SubscriberGate.tsx — MER Algo Footprints
 *
 * Client-side commercial-access gate. UX and defence in depth only — the
 * server's `requireEntitlement` is the authoritative boundary, and a disabled
 * script or a direct API call must still meet it.
 *
 * ── What it decides on ────────────────────────────────────────────────────────
 *
 * Phase-2 P2-D13 IMPL-4 (D-P2AT-26). It dispatches on the explicit
 * `entitlement.entitlementState` and nothing else — no role, no market-data
 * tier, no plan. There is NO administrator bypass: role is not entitlement
 * (D-P2AR-23), and administration lives in the account shell, outside this
 * gate.
 *
 *   entitled      → the commercial application
 *   not_entitled  → "Access to MER"
 *   unavailable   → "Unable to verify access", one automatic retry, then manual
 *   unknown       → "Checking access…" while /api/me is read once
 *
 * ── DORMANT ───────────────────────────────────────────────────────────────────
 *
 * Mounted on no route. `src/index.tsx` places the commercial application behind
 * a pass-through `CommercialBoundary`; enforcement activation — a separately
 * governed gate — replaces that pass-through with this component.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth, type EntitlementState } from './AuthContext';
import { AccessCheckingScreen, AccessNotEnabledScreen, AccessUnverifiedScreen } from './AccessStateScreens';

export type AccessDecision = 'allow' | 'checking' | 'not_enabled' | 'unavailable';

/**
 * The whole policy, as a pure function. Only 'entitled' is allowed; an
 * unrecognised state is treated as still checking, never as allowed.
 */
export function accessDecision(state: EntitlementState): AccessDecision {
  switch (state) {
    case 'entitled':     return 'allow';
    case 'not_entitled': return 'not_enabled';
    case 'unavailable':  return 'unavailable';
    default:             return 'checking';
  }
}

/** Delay before the single automatic retry of an unavailable answer. */
const AUTO_RETRY_DELAY_MS = 5000;

export function SubscriberGate() {
  const { entitlement, refreshEntitlement } = useAuth();
  const decision = accessDecision(entitlement.entitlementState);

  // Refs, not state: they must survive re-renders so the budget stays bounded.
  const hydrationRequested = useRef(false);
  const autoRetried        = useRef(false);
  const [retrying, setRetrying] = useState(false);

  const retry = useCallback(async () => {
    setRetrying(true);
    try {
      await refreshEntitlement();
    } finally {
      setRetrying(false);
    }
  }, [refreshEntitlement]);

  // UNKNOWN: ask /api/me once. refreshEntitlement always settles the state —
  // entitled, not entitled or unavailable — so this cannot spin.
  useEffect(() => {
    if (decision !== 'checking' || hydrationRequested.current) return;
    hydrationRequested.current = true;
    void refreshEntitlement();
  }, [decision, refreshEntitlement]);

  // UNAVAILABLE: exactly one automatic retry, then the manual button only.
  // The flag is set when the retry FIRES, not when it is scheduled, so an
  // effect torn down early (StrictMode, a fast state change) cannot use up the
  // single retry without performing it.
  useEffect(() => {
    if (decision !== 'unavailable' || autoRetried.current) return undefined;
    const timer = setTimeout(() => {
      autoRetried.current = true;
      void retry();
    }, AUTO_RETRY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [decision, retry]);

  if (decision === 'allow')       return <Outlet />;
  if (decision === 'not_enabled') return <AccessNotEnabledScreen />;
  if (decision === 'unavailable') return <AccessUnverifiedScreen onRetry={retry} retrying={retrying} />;
  return <AccessCheckingScreen />;
}
